import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Smartphone, Globe, Calendar, Loader2, TrendingUp, AlertCircle, CreditCard } from 'lucide-react';
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore';
import { db, getRevenueReport } from '../../../services/firebaseService';
import { fetchEmailTemplates } from '../../../services/campaignService';

/** Get ISO 8601 week number (1-53) */
function getISOWeek(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

interface WeekData {
    week: number;
    free: number;
    tester: number;
    paid: number;
    total: number;
    active: number;
}

interface DashboardStats {
    totalUsers: number;
    paidUsers: number;
    conversionPct: number;
    androidUsers: number;
    iosUsers: number;
    webUsers: number;
    activeLast7Days: number;
    weeklyByYear: Record<number, WeekData[]>;
    campaignReach: Array<{ id: string; name: string; count: number }>;
}

const AVAILABLE_YEARS = [2024, 2025, 2026];

const AdminCampaignDashboard: React.FC = () => {
    const { t } = useTranslation();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const templates = await fetchEmailTemplates();
            const templateNameMap: Record<string, string> = {};
            templates.forEach(t => { templateNameMap[t.id] = t.name; });

            // Fetch ALL users (no limit)
            const usersRef = collection(db, 'users');
            const q = query(usersRef, orderBy('creationTimestamp', 'desc'));
            const snap = await getDocs(q);

            const weeklyByYear: Record<number, Record<number, { free: number; tester: number; paid: number; active: number }>> = {};
            const platformCounts = { android: 0, ios: 0, web: 0 };
            let paidTotal = 0;
            let activeLast7Days = 0;
            const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

            snap.docs.forEach(doc => {
                const data = doc.data();

                if (data.platform === 'android') platformCounts.android++;
                else if (data.platform === 'ios') platformCounts.ios++;
                else platformCounts.web++;
                if (data.subscriptionStatus === 'paid' && !data.isTester && !data.subscriptionSandbox) paidTotal++;
                if (typeof data.lastLoginAt === 'number' && data.lastLoginAt >= sevenDaysAgo) activeLast7Days++;

                if (data.creationTimestamp) {
                    const d = new Date(data.creationTimestamp);
                    const year = d.getFullYear();
                    const week = getISOWeek(d);
                    if (!weeklyByYear[year]) weeklyByYear[year] = {};
                    if (!weeklyByYear[year][week]) weeklyByYear[year][week] = { free: 0, tester: 0, paid: 0, active: 0 };

                    // Samme «ekte betalende»-definisjon som OMSETNING/getRevenueReport
                    const realPaid = data.subscriptionStatus === 'paid' && !data.isTester
                        && !data.subscriptionSandbox && !data.subscriptionIsTrial
                        && data.subscriptionLiveVerified !== false;
                    if (realPaid) {
                        weeklyByYear[year][week].paid++;
                    } else if (data.isTester) {
                        weeklyByYear[year][week].tester++;
                    } else {
                        weeklyByYear[year][week].free++;
                    }
                }

                // Aktivitet per uke (uavhengig av registreringsdato)
                if (Array.isArray(data.activeWeeks)) {
                    data.activeWeeks.forEach((key: any) => {
                        if (typeof key !== 'string') return;
                        const m = key.match(/^(\d{4})-W(\d{2})$/);
                        if (!m) return;
                        const y = parseInt(m[1]);
                        const w = parseInt(m[2]);
                        if (!weeklyByYear[y]) weeklyByYear[y] = {};
                        if (!weeklyByYear[y][w]) weeklyByYear[y][w] = { free: 0, tester: 0, paid: 0, active: 0 };
                        weeklyByYear[y][w].active++;
                    });
                }
            });

            // Convert to arrays with all 52 weeks
            const weeklyArrays: Record<number, WeekData[]> = {};
            for (const [yearStr, weekMap] of Object.entries(weeklyByYear)) {
                const year = parseInt(yearStr);
                weeklyArrays[year] = Array.from({ length: 52 }, (_, i) => {
                    const w = weekMap[i + 1] || { free: 0, tester: 0, paid: 0, active: 0 };
                    return { week: i + 1, free: w.free, tester: w.tester, paid: w.paid, active: w.active, total: w.free + w.tester + w.paid };
                });
            }

            // Campaign reach
            const sentRef = collection(db, 'campaign_sent');
            const sentQuery = query(sentRef, orderBy('sent_at', 'desc'), limit(2000));
            const sentSnap = await getDocs(sentQuery);
            const campaignCounts: Record<string, number> = {};
            sentSnap.docs.forEach(doc => {
                const data = doc.data();
                if (data.template_id) {
                    campaignCounts[data.template_id] = (campaignCounts[data.template_id] || 0) + 1;
                }
            });
            const reachArray = Object.entries(campaignCounts)
                .map(([id, count]) => ({ id, name: templateNameMap[id] || id, count }))
                .sort((a, b) => b.count - a.count);

            // Kanonisk betalende-tall fra samme kilde som OMSETNING (getRevenueReport).
            // Faller tilbake til lokal telling hvis kallet feiler.
            let canonicalPaid = paidTotal;
            let conversionPct = snap.size > 0 ? (paidTotal / snap.size) * 100 : 0;
            try {
                const report = await getRevenueReport();
                canonicalPaid = report.counts.activePaying;
                const denom = report.counts.activePaying + report.counts.free;
                conversionPct = denom > 0 ? (report.counts.activePaying / denom) * 100 : 0;
            } catch (e) {
                console.warn('getRevenueReport feilet i dashboard, bruker lokal telling:', e);
            }

            setStats({
                totalUsers: snap.size,
                paidUsers: canonicalPaid,
                conversionPct,
                androidUsers: platformCounts.android,
                iosUsers: platformCounts.ios,
                webUsers: platformCounts.web,
                activeLast7Days,
                weeklyByYear: weeklyArrays,
                campaignReach: reachArray,
            });
        } catch (e: any) {
            console.error("Dashboard fetch error:", e);
            setError(e.message || "Kunne ikke hente statistikk");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Loader2 size={24} className="animate-spin text-[var(--text-muted)]" />
                <p className="text-xs text-[var(--text-muted)] font-medium tracking-wide uppercase">Laster statistikk...</p>
            </div>
        );
    }

    if (error || !stats) {
        return (
            <div className="p-6 bg-red-500/5 border border-red-500/10 rounded-2xl flex items-start gap-4">
                <AlertCircle className="text-red-400 shrink-0" size={18} />
                <div>
                    <p className="text-sm font-bold text-red-400">Statistikk utilgjengelig</p>
                    <p className="text-xs text-red-400/70 mt-1">{error}</p>
                </div>
            </div>
        );
    }

    const weeks = stats.weeklyByYear[selectedYear] || [];
    const yearTotal = weeks.reduce((sum, w) => sum + w.total, 0);
    const activeWeeks = weeks.filter(w => w.total > 0).length;
    const avgPerWeek = activeWeeks > 0 ? (yearTotal / activeWeeks) : 0;
    const maxWeekTotal = Math.max(...weeks.map(w => w.total), 1);
    const maxWeekActive = Math.max(...weeks.map(w => w.active), 1);

    // Thousands-separated integers (nb-NO), matching AdminRevenueDashboard's number formatting.
    const fmtNum = (n: number | null | undefined) => (n ?? 0).toLocaleString('nb-NO');

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
            {/* Overview Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-violet-500/10 flex items-center justify-center">
                            <Users size={16} className="text-violet-400" />
                        </div>
                        <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Totalt</p>
                    </div>
                    <p className="text-3xl font-black text-[var(--text-primary)]">{fmtNum(stats.totalUsers)}</p>
                    <p className="text-2xs text-[var(--text-muted)] mt-1">Alle brukere</p>
                </div>

                <div className="bg-amber-500/5 border border-amber-500/20 p-5 rounded-3xl">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center">
                            <CreditCard size={16} className="text-amber-400" />
                        </div>
                        <p className="text-2xs font-black text-amber-400 uppercase tracking-widest">Betalende</p>
                    </div>
                    <p className="text-3xl font-black text-amber-400">{fmtNum(stats.paidUsers)}</p>
                    <div className="w-full bg-[var(--bg-input)] h-1 rounded-full mt-3 overflow-hidden">
                        <div
                            className="bg-amber-500 h-full rounded-full transition-all duration-1000"
                            style={{ width: `${Math.min(stats.conversionPct, 100)}%` }}
                        />
                    </div>
                    <p className="text-2xs text-amber-400/60 mt-1">{stats.conversionPct.toFixed(1)}% konvertering</p>
                </div>

                <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                            <Smartphone size={16} className="text-emerald-400" />
                        </div>
                        <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Android</p>
                    </div>
                    <p className="text-3xl font-black text-[var(--text-primary)]">{fmtNum(stats.androidUsers)}</p>
                    <div className="w-full bg-[var(--bg-input)] h-1 rounded-full mt-3 overflow-hidden">
                        <div
                            className="bg-emerald-500 h-full rounded-full transition-all duration-1000"
                            style={{ width: `${stats.totalUsers > 0 ? (stats.androidUsers / stats.totalUsers) * 100 : 0}%` }}
                        />
                    </div>
                </div>

                <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-blue-500/10 flex items-center justify-center">
                            <Smartphone size={16} className="text-blue-400" />
                        </div>
                        <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">iOS</p>
                    </div>
                    <p className="text-3xl font-black text-[var(--text-primary)]">{fmtNum(stats.iosUsers)}</p>
                    <div className="w-full bg-[var(--bg-input)] h-1 rounded-full mt-3 overflow-hidden">
                        <div
                            className="bg-blue-500 h-full rounded-full transition-all duration-1000"
                            style={{ width: `${stats.totalUsers > 0 ? (stats.iosUsers / stats.totalUsers) * 100 : 0}%` }}
                        />
                    </div>
                </div>

                <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center">
                            <Globe size={16} className="text-amber-400" />
                        </div>
                        <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Web</p>
                    </div>
                    <p className="text-3xl font-black text-[var(--text-primary)]">{fmtNum(stats.webUsers)}</p>
                    <div className="w-full bg-[var(--bg-input)] h-1 rounded-full mt-3 overflow-hidden">
                        <div
                            className="bg-amber-500 h-full rounded-full transition-all duration-1000"
                            style={{ width: `${stats.totalUsers > 0 ? (stats.webUsers / stats.totalUsers) * 100 : 0}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Weekly Registration Chart with Year Toggle */}
            <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-8">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center">
                            <Calendar size={20} className="text-indigo-400" />
                        </div>
                        <div>
                            <p className="text-lg font-black text-[var(--text-primary)]">Medlemsvekst</p>
                            <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">
                                Nye registreringer per uke – {selectedYear}
                            </p>
                        </div>
                    </div>

                    {/* Year toggle */}
                    <div className="flex items-center gap-1.5 p-1 bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-xl">
                        {AVAILABLE_YEARS.map(year => (
                            <button
                                key={year}
                                onClick={() => setSelectedYear(year)}
                                className={`px-3 py-1.5 rounded-lg text-2xs font-black uppercase tracking-widest transition-all ${
                                    selectedYear === year
                                        ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30'
                                        : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)] border border-transparent'
                                }`}
                            >
                                {year}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Year summary */}
                <div className="flex items-center gap-8 mb-6 flex-wrap">
                    <div>
                        <p className="text-2xl font-black text-[var(--text-primary)]">{fmtNum(yearTotal)}</p>
                        <p className="text-3xs text-[var(--text-muted)] font-bold uppercase tracking-widest">Totalt {selectedYear}</p>
                    </div>
                    <div>
                        <p className="text-2xl font-black text-[var(--text-secondary)]">{avgPerWeek.toFixed(1)}</p>
                        <p className="text-3xs text-[var(--text-muted)] font-bold uppercase tracking-widest">Snitt per uke</p>
                    </div>
                    <div>
                        <p className="text-2xl font-black text-indigo-400">{fmtNum(stats.activeLast7Days)}</p>
                        <p className="text-3xs text-[var(--text-muted)] font-bold uppercase tracking-widest">Aktive siste 7d</p>
                    </div>
                </div>

                {/* Legend */}
                <div className="flex items-center gap-5 mb-4 flex-wrap">
                    <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-sm bg-stone-500" />
                        <span className="text-3xs text-[var(--text-secondary)] font-bold uppercase tracking-widest">Gratis</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-sm bg-amber-500" />
                        <span className="text-3xs text-[var(--text-secondary)] font-bold uppercase tracking-widest">Testere</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
                        <span className="text-3xs text-[var(--text-secondary)] font-bold uppercase tracking-widest">Abonnenter</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-sm bg-indigo-500/30 border border-indigo-500/50" />
                        <span className="text-3xs text-[var(--text-secondary)] font-bold uppercase tracking-widest">Aktive (unike)</span>
                    </div>
                </div>

                {/* Weekly stacked bar chart */}
                <div className="h-48 flex items-end gap-0.5 px-1 pb-6">
                    {weeks.length === 0 ? (
                        <div className="w-full h-full flex items-center justify-center border border-dashed border-[var(--border-primary)] rounded-2xl">
                            <p className="text-2xs text-[var(--text-muted)] font-black uppercase tracking-widest">
                                Ingen data for {selectedYear}
                            </p>
                        </div>
                    ) : weeks.map((item) => {
                        const heightPct = (item.total / maxWeekTotal) * 100;
                        const freePct = item.total > 0 ? (item.free / item.total) * heightPct : 0;
                        const testerPct = item.total > 0 ? (item.tester / item.total) * heightPct : 0;
                        const paidPct = item.total > 0 ? (item.paid / item.total) * heightPct : 0;
                        const activeHeightPct = (item.active / maxWeekActive) * 100;
                        return (
                            <div key={item.week} className="flex-1 h-full group relative flex flex-col items-center justify-end">
                                {/* Bakgrunns-stolpe: aktive unike brukere (egen skala) */}
                                {item.active > 0 && (
                                    <div
                                        className="absolute bottom-0 left-0 right-0 bg-indigo-500/15 group-hover:bg-indigo-500/30 transition-colors rounded-t-sm pointer-events-none"
                                        style={{ height: `${Math.max(activeHeightPct, 2)}%` }}
                                    />
                                )}
                                <div
                                    className="w-full flex flex-col-reverse rounded-t-sm transition-all duration-500 relative z-[1]"
                                    style={{ height: `${Math.max(heightPct, 2)}%`, minHeight: '2px' }}
                                >
                                    {/* Stacked segments: free (bottom), tester (middle), paid (top) */}
                                    {item.total > 0 ? (
                                        <>
                                            {item.free > 0 && (
                                                <div
                                                    className="w-full bg-stone-500/40 group-hover:bg-stone-500/70 transition-colors"
                                                    style={{ height: `${(freePct / heightPct) * 100}%`, minHeight: '1px' }}
                                                />
                                            )}
                                            {item.tester > 0 && (
                                                <div
                                                    className="w-full bg-amber-500/50 group-hover:bg-amber-500/80 transition-colors"
                                                    style={{ height: `${(testerPct / heightPct) * 100}%`, minHeight: '1px' }}
                                                />
                                            )}
                                            {item.paid > 0 && (
                                                <div
                                                    className="w-full bg-emerald-500/50 group-hover:bg-emerald-500/80 transition-colors rounded-t-sm"
                                                    style={{ height: `${(paidPct / heightPct) * 100}%`, minHeight: '1px' }}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <div className="w-full h-full bg-[var(--bg-input)]/30" />
                                    )}
                                    {(item.total > 0 || item.active > 0) && (
                                        <>
                                            <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[7px] font-black whitespace-nowrap z-10 flex items-center gap-0.5">
                                                {item.total > 0 && <span className="text-[var(--text-secondary)]">{item.total}</span>}
                                                {item.active > 0 && <span className="text-indigo-400">·{item.active}</span>}
                                            </div>
                                            <div className="absolute -top-14 left-1/2 -translate-x-1/2 bg-[var(--bg-input)] border border-[var(--border-secondary)] text-[var(--text-primary)] text-3xs font-black px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20">
                                                U{item.week}: {item.total} nye
                                                {item.paid > 0 && <span className="text-emerald-400"> · {item.paid} abo</span>}
                                                {item.tester > 0 && <span className="text-amber-400"> · {item.tester} test</span>}
                                                {item.active > 0 && <span className="text-indigo-400"> · {item.active} aktive</span>}
                                            </div>
                                        </>
                                    )}
                                </div>
                                {(item.week % 4 === 1 || item.week === 52) && (
                                    <span className="text-[7px] text-[var(--text-muted)] font-black mt-2 absolute -bottom-5 whitespace-nowrap">
                                        U{item.week}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Campaign Reach Table */}
            <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel overflow-hidden">
                <div className="p-8 border-b border-[var(--border-primary)]">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-2xl bg-pink-500/10 flex items-center justify-center">
                            <TrendingUp size={20} className="text-pink-400" />
                        </div>
                        <div>
                            <p className="text-lg font-black text-[var(--text-primary)]">E-post utsendelser</p>
                            <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">Antall mottatte e-poster per mal</p>
                        </div>
                    </div>
                </div>

                <div className="p-4">
                    {stats.campaignReach.length === 0 ? (
                        <div className="py-12 text-center text-[var(--text-muted)]">
                            <p className="text-tiny uppercase tracking-tighter">Ingen kampanje-e-poster sendt nylig</p>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {stats.campaignReach.map((item) => (
                                <div key={item.id} className="flex items-center justify-between p-4 hover:bg-[var(--bg-surface-alt)] rounded-2xl transition-colors">
                                    <div className="flex items-center gap-4">
                                        <div className="w-8 h-8 rounded-full bg-[var(--bg-input)] flex items-center justify-center text-2xs font-black text-[var(--text-secondary)] shrink-0">
                                            {item.id.substring(0, 2).toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs font-bold text-[var(--text-primary)] truncate">{item.name}</p>
                                            <p className="text-3xs text-[var(--text-muted)] font-mono truncate">{item.id}</p>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-sm font-black text-[var(--text-primary)]">{item.count}</p>
                                        <p className="text-3xs text-[var(--text-muted)] font-bold uppercase tracking-widest">Mottatt</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminCampaignDashboard;
